import clsx from 'clsx'
import {CUSTOM_SELECT_ALL_VALUE} from 'common/constants'
import useDeepStable from 'common/hooks/useDeepStable'
import useStableCallback from 'common/hooks/useStableCallback'
import React, {FC, memo, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import Select, {ActionMeta} from 'react-select'

import {SELECT_CLASSNAMES, SELECT_COMPONENTS} from './selectParts'

/**
 * Payload handed to the wrapper whenever the user changes the selection.
 *
 * - `values`: the new selection as plain value(s) — an array for multi, a
 *   scalar for single. This is what should be written to form state.
 * - `options`: the option objects react-select / the select-all handler
 *   produced, in the exact shape the legacy CustomSelect forwarded to caller
 *   onChange handlers (already-selected values missing from the option list
 *   arrive as label-less `{value}` objects — 166 call sites depend on this
 *   contract, do not "fix" it here).
 * - `valueChanged`: false only for the select-all edge case where no options
 *   matched the current search; legacy behavior notified onChange without
 *   touching the form value.
 */
export interface SelectionChange {
  values: any
  options: any
  actionMeta: ActionMeta<any>
  valueChanged: boolean
}

export interface BaseSelectProps extends React.ComponentProps<typeof Select> {
  /** Current selection: scalar, array of scalars, or comma-separated string. */
  currentValue?: any
  onSelectionChange: (change: SelectionChange) => void
  optionValue?: string
  hideSelectAll?: boolean
  /** Already-translated label, used as the placeholder fallback. */
  labelText?: string
  /** Already-translated placeholder. */
  placeholderText?: string
  /** Extra classes for the react-select control (e.g. `is-invalid`). */
  selectClassName?: string
  filterOption?: any
}

const MemoizedSelect = memo(Select) as unknown as typeof Select

/**
 * Case-insensitive substring match that tolerates null/undefined option fields.
 * Options built straight from API rows regularly carry a null label or value
 * (`{label: session.name, value: session.db_postfix}`), and react-select runs
 * filterOption over every one of them.
 */
const matchesSearch = (fieldValue: any, needle: string): boolean =>
  fieldValue !== null &&
  fieldValue !== undefined &&
  String(fieldValue).toLowerCase().includes(needle)

/**
 * Loose comparison used to resolve stored form values back to option objects.
 * Loose on purpose (`'1' == 1` has to match), but null/undefined never resolve
 * to an option — otherwise an option with a null value would report itself as
 * selected while the field is still empty.
 */
const isSameValue = (optionValue: any, formValue: any): boolean => {
  if (optionValue === null || optionValue === undefined) return false
  if (formValue === null || formValue === undefined) return false

  return optionValue == formValue
}

/** Normalize a form value (scalar / array / comma-string) to an array of scalars. */
const toValueArray = (value: any): any[] => {
  if (Array.isArray(value)) return value
  if (typeof value === 'string' && value !== '') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '')
  }
  return []
}

/**
 * The engine shared by CustomSelect (Formik) and Fields/Select (controlled):
 * option filtering/search, select-all / deselect-all handling, and resolution
 * of stored values back to option objects. Wrappers own form-state glue and
 * the surrounding label/helper/error markup.
 */
const BaseSelect: FC<BaseSelectProps> = ({
  currentValue,
  onSelectionChange,
  options: optionsProp,
  optionValue = 'value',
  hideSelectAll = false,
  labelText,
  placeholderText,
  selectClassName,
  filterOption,
  closeMenuOnSelect,
  isMulti,
  ...restProps
}) => {
  const intl = useIntl()
  const [searchField, setSearchField] = useState<string | undefined>(undefined)
  const [filteredOptionsCount, setFilteredOptionsCount] = useState(0)

  // Callers commonly pass `options={list.map(...)}` — a fresh array identity
  // every render. Deep-stabilize so the memoized Select below isn't forced to
  // re-render on every unrelated Formik keystroke.
  const options = useDeepStable(optionsProp)

  const isIncludingString = useStableCallback((option: any, searchText: string) => {
    // Keep the select-all row visible while enough options match the search
    if (option?.value === CUSTOM_SELECT_ALL_VALUE && filteredOptionsCount > 2) {
      return true
    }

    let check = true
    if (searchText) {
      const needle = searchText.toLowerCase().trim()
      check &&= matchesSearch(option?.label, needle) || matchesSearch(option?.value, needle)
    }

    if (typeof filterOption === 'function') {
      check &&= filterOption(option, searchText)
    }
    return check
  })

  const areAllFilteredOptionsSelected = useMemo(() => {
    if (!isMulti || !options) return false

    const filteredOptions = (options || []).filter(
      (opt: any) =>
        opt.value !== CUSTOM_SELECT_ALL_VALUE && isIncludingString(opt, searchField || '')
    )
    if (filteredOptions.length === 0) return false

    const fieldValue = toValueArray(currentValue)
    if (fieldValue.length === 0) return false

    const selectedSet = new Set(fieldValue.map((v: any) => String(v)))
    return filteredOptions.every((opt: any) => selectedSet.has(String(opt.value)))
  }, [options, currentValue, searchField, isMulti, isIncludingString])

  const selectedOptions = useMemo(() => {
    if (!options) {
      return undefined
    }
    if (isMulti) {
      const fieldValue = Array.isArray(currentValue) ? currentValue : toValueArray(currentValue)
      const resolved = []
      for (let i = 0; i < fieldValue.length; i++) {
        const option = options.find((opt: any) => isSameValue(opt[optionValue], fieldValue[i]))
        // Values with no matching option (stale data, async gaps) are not
        // rendered — pushing `undefined` made react-select call
        // getOptionLabel(undefined) in the value container.
        if (option !== undefined) resolved.push(option)
      }
      return resolved
    }
    return options.find((opt: any) => isSameValue(opt[optionValue], currentValue))
  }, [options, isMulti, currentValue, optionValue])

  useEffect(() => {
    const filteredOptions = options?.filter((opt) => isIncludingString(opt, searchField || ''))
    setFilteredOptionsCount(filteredOptions?.length || 0)
  }, [isIncludingString, options, searchField, filterOption])

  // Inject (or relabel) the select-all row for multi-selects
  const processedOptions = useMemo(() => {
    if (!isMulti || !options || options.length === 0 || hideSelectAll) return options

    const selectAllLabel = areAllFilteredOptionsSelected
      ? intl.formatMessage({id: 'GENERAL.DESELECT_ALL', defaultMessage: 'Deselect All'})
      : intl.formatMessage({id: 'GENERAL.SELECT_ALL', defaultMessage: 'Select All'})

    const hasSelectAll = options.some((option: any) => option?.value === CUSTOM_SELECT_ALL_VALUE)
    if (!hasSelectAll) {
      return [
        {
          label: selectAllLabel,
          value: CUSTOM_SELECT_ALL_VALUE,
          isDeselectAll: areAllFilteredOptionsSelected,
        },
        ...options,
      ]
    }

    return options.map((option: any) => {
      if (option.value === CUSTOM_SELECT_ALL_VALUE) {
        return {...option, label: selectAllLabel, isDeselectAll: areAllFilteredOptionsSelected}
      }
      return option
    })
  }, [options, isMulti, hideSelectAll, areAllFilteredOptionsSelected, intl])

  const handleInputChange = useStableCallback((newValue: string, {action}: {action: string}) => {
    if (action === 'input-change') {
      setSearchField(newValue)
    }
  })

  const handleMenuClose = useStableCallback(() => {
    setSearchField(undefined)
    setFilteredOptionsCount(0)
  })

  const handleSelectChange = useStableCallback((option: any, actionMeta: ActionMeta<any>) => {
    if (isMulti) {
      // Select All / Deselect All row clicked
      if (actionMeta.option && actionMeta.option?.value === CUSTOM_SELECT_ALL_VALUE) {
        let filteredOptions = (processedOptions || []).filter(
          (newOpt: any) => newOpt.value !== CUSTOM_SELECT_ALL_VALUE
        )
        filteredOptions = filteredOptions.filter((opt) => isIncludingString(opt, searchField || ''))

        const alreadySelected = toValueArray(currentValue)
        let values: any[] | null = null

        if (filteredOptions.length > 0) {
          const filteredValues = filteredOptions.map((o: any) => String(o.value))
          const alreadySelectedSet = new Set(alreadySelected.map((s) => String(s)))
          const allSelected = filteredValues.every((fv) => alreadySelectedSet.has(fv))

          if (allSelected) {
            // Deselect every option matching the current search
            values = alreadySelected.filter((v) => !filteredValues.includes(String(v)))
            option = values.map((v) => ({value: v}))
          } else {
            // Select every option matching the current search, keeping any
            // already-selected values that fall outside it
            for (const v of alreadySelected) {
              const exists = filteredOptions.some((o: any) => String(o.value) === String(v))
              if (!exists) filteredOptions.push({value: v})
            }
            option = filteredOptions
            values = filteredOptions.map((o: any) => o.value)
          }
        }

        onSelectionChange({
          values: values ?? alreadySelected,
          options: option,
          actionMeta,
          valueChanged: values !== null,
        })
        return
      }

      onSelectionChange({
        values: option.map((o: any) => o.value),
        options: option,
        actionMeta,
        valueChanged: true,
      })
    } else {
      setSearchField(undefined)
      onSelectionChange({
        values: option?.value,
        options: option,
        actionMeta,
        valueChanged: true,
      })
    }
  })

  const placeholderContent = useMemo(
    () =>
      placeholderText ? (
        <span className='text-muted'>{placeholderText}</span>
      ) : labelText ? (
        <span className='text-muted'>{labelText}</span>
      ) : (
        'Select an option'
      ),
    [placeholderText, labelText]
  )

  const stableSelectClassName = useMemo(
    () => clsx('react-select-styled p-0', selectClassName),
    [selectClassName]
  )

  return (
    <MemoizedSelect
      components={SELECT_COMPONENTS}
      classNamePrefix='react-select'
      className={stableSelectClassName}
      classNames={SELECT_CLASSNAMES}
      isMulti={isMulti}
      options={processedOptions}
      value={selectedOptions}
      onInputChange={handleInputChange}
      inputValue={searchField}
      onMenuClose={handleMenuClose}
      onChange={handleSelectChange}
      filterOption={isIncludingString}
      closeMenuOnSelect={(closeMenuOnSelect ?? isMulti) ? false : undefined}
      placeholder={placeholderContent}
      hideSelectedOptions={false}
      {...restProps}
    />
  )
}

export default BaseSelect
